import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { json } from '../api/apiClient';

/**
 * Hook: Deck-Liste laden (virtuell + physisch).
 * Wird für die Deck- und Quellen-Filter auf der Wantlist-Seite genutzt.
 */
export function useDeckList() {
  return useQuery({
    queryKey: ['decks', 'list', 'all'],
    queryFn: () => json('/api/decks/list'),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook: Wantlist-Daten laden
 *
 * @param {number[]} [deckIds]                  Optional: nur diese Decks einbeziehen
 * @param {number[]} [sourcePhysicalDeckIds]    Optional: physische Decks als Sammlungs-Quelle
 * @param {boolean}  [includeOwned=false]       Auch bereits besessene Karten zurückgeben
 */
export function useWantlistData(deckIds, sourcePhysicalDeckIds, includeOwned = false) {
  const params = new URLSearchParams();
  if (deckIds?.length) params.set('deckIds', deckIds.join(','));
  if (sourcePhysicalDeckIds?.length) params.set('sourcePhysicalDeckIds', sourcePhysicalDeckIds.join(','));
  if (includeOwned) params.set('includeOwned', 'true');

  const qs = params.toString();
  // Wenn der User explizit eine leere Deck-Liste wählt (Array, aber length=0),
  // ist das semantisch "keine Decks" — nicht "alle Decks". Backend würde
  // ohne Parameter "alle" zurückliefern, daher kurzschließen wir das hier.
  const explicitEmpty = Array.isArray(deckIds) && deckIds.length === 0;

  return useQuery({
    queryKey: [
      'wantlist',
      explicitEmpty
        ? 'empty'
        : deckIds?.slice().sort().join(',') || 'all',
      sourcePhysicalDeckIds?.slice().sort().join(',') || 'none',
      includeOwned,
    ],
    queryFn: () => {
      if (explicitEmpty) {
        return Promise.resolve({ cards: [], deckCategories: {}, tokens: [] });
      }
      return json(`/api/wantlist${qs ? `?${qs}` : ''}`);
    },
    staleTime: 1000 * 60 * 2, // 2 Minuten
  });
}

/**
 * Hook: Print eines Deck-Eintrags durch eine andere Variante ersetzen
 */
export function useReplacePrint() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deckId, scryfallId, newScryfallId }) =>
      json(`/api/decks/${deckId}/cards/${encodeURIComponent(scryfallId)}/replace-print`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newScryfallId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wantlist'] });
      queryClient.invalidateQueries({ queryKey: ['deck'] });
      // Der Deck Editor verwendet einen separaten Cache-Key-Prefix
      queryClient.invalidateQueries({ queryKey: ['deck-editor'] });
    },
  });
}

/**
 * Hook: Wantlist-spezifische Karten-Metadaten aktualisieren
 * (z.B. printLocked, foilPreference, languagePreference, conditionPreference)
 */
export function useUpdateCardMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deckCardId, metadata }) =>
      json(`/api/wantlist/cards/${deckCardId}/metadata`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metadata),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wantlist'] });
      // Metadata-Änderungen können auch Editor-Anzeige beeinflussen (z.B. printLocked)
      queryClient.invalidateQueries({ queryKey: ['deck-editor'] });
    },
  });
}
